import { graphql, useStaticQuery } from "gatsby";
import { KontentTreeDetailPage, Image } from "../types";
import { formatImages } from "./images";

export interface UseTrees {
  trees: Tree[];
  collections: {
    [key: string]: {
      name: string;
      trees: Tree[];
    };
  };
}

export interface Tree {
  page?: {
    url?: string;
    title?: string;
  };
  systemId?: string;
  image?: Image;
  scientificName?: string;
  commonName?: string;
  treeId?: string;
  collections?: {
    codename?: string;
    value?: string;
  }[];
  location: {
    latitude: number;
    longitude: number;
  };
  icon?: {
    codename: string;
  }[];
}

export const formatTree = (
  treeContent: KontentTreeDetailPage["elements"]["content"]["value"][0]
) => {
  const {
    thumbnail_image,
    hero_image,
    common_name,
    scientific_name,
    tree_collection,
    latitude,
    longitude,
    map_icon,
    tree_id,
  } = treeContent.elements;

  const thumbnailImage =
    thumbnail_image?.value[0]?.elements &&
    formatImages(thumbnail_image.value[0]?.elements).largeImage;
  const heroImage =
    !thumbnailImage &&
    hero_image?.value[0]?.elements &&
    formatImages(hero_image.value[0].elements).largeImage;

  return {
    systemId: treeContent.system?.id,
    image: thumbnailImage || heroImage || undefined,
    scientificName: scientific_name?.value,
    commonName: common_name?.value,
    treeId: tree_id?.value,
    collections: tree_collection,
    location: {
      latitude: latitude?.value,
      longitude: longitude?.value,
    },
    icon: map_icon?.value[0]?.codename || "deciduous",
  };
};

interface TreeQuery {
  allKontentItemPage: {
    edges: {
      node: KontentTreeDetailPage;
    }[];
  };
}

export const useTrees = (): UseTrees => {
  const treesData = useStaticQuery<TreeQuery>(graphql`
    query TreePagesComplete {
      allKontentItemPage(
        filter: {
          elements: {
            content: {
              value: {
                elemMatch: { system: { type: { eq: "content___tree_detail" } } }
              }
            }
          }
        }
      ) {
        edges {
          node {
            id
            elements {
              title {
                value
              }
              url {
                url
              }
              content {
                value {
                  ...KontentTreeDetail
                }
              }
            }
          }
        }
      }
    }
  `);

  if (treesData.allKontentItemPage.edges.length === 0) {
    return {
      trees: [],
      collections: {},
    };
  }

  const trees = treesData.allKontentItemPage?.edges.map((treeNode) => {
    const treeData = formatTree(treeNode.node.elements?.content.value[0]);

    const pageInfo = {
      url: treeNode.node?.elements?.url?.url,
      title: treeNode.node?.elements?.title?.value,
    };

    return {
      page: pageInfo,
      ...treeData,
    };
  });

  const collections = {};

  trees.forEach((tree) => {
    tree.collections?.value.forEach((collection) => {
      if (!collection || !collection.codename) return null;

      collections[collection.codename]
        ? collections[collection.codename].trees.push(tree)
        : (collections[collection.codename] = {
            trees: [tree],
            name: collection.name,
          });
    });
  });

  return {
    trees: trees,
    collections: collections,
  };
};
